import { memo, useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import type { MapSection } from '../utils/filterHelpers';

export interface WebsiteLink {
  id: string;
  title: string;
  url: string;
  imageUrl: string;
  description?: string;
  categories: string[];
  continent?: string;
  countries?: string[];
  states?: string[];
  districts?: string[];
  cities?: string[];
  tags?: string[];
  language?: string;
  mapProvider?: string;
  world?: boolean;
  owner?: Array<{ name: string; url: string }>;
}

const getLocationDisplay = (
  website: WebsiteLink, 
  showLocationInfo: boolean,
  selectedContinents?: string[],
  selectedCountries?: string[],
  selectedCities?: string[]
): string | null => {
  if (!showLocationInfo) return null;
  
  if (selectedCities && selectedCities.length > 0 && website.cities && website.cities.length > 1) {
    const matchingCity = website.cities.find(city => selectedCities.includes(city));
    if (matchingCity) return matchingCity;
  }
  
  if (selectedCountries && selectedCountries.length > 0 && website.countries && website.countries.length > 1) {
    const matchingCountry = website.countries.find(country => selectedCountries.includes(country));
    if (matchingCountry) return matchingCountry;
  }
  
  if (website.cities && website.cities.length > 0) {
    return website.cities[0];
  }
  if (website.countries && website.countries.length > 0) {
    return website.countries[0];
  }
  if (website.continent) {
    return website.continent;
  }
  if (website.world) {
    return "World";
  }
  return null;
};

const getAdditionalLocationCount = (website: WebsiteLink, showLocationInfo: boolean): number => {
  if (!showLocationInfo) return 0;
  
  if (website.cities && website.cities.length > 1) {
    return website.cities.length - 1;
  }
  if (website.countries && website.countries.length > 1) {
    return website.countries.length - 1;
  }
  return 0;
};

// Helper to check if a map has multiple locations
const hasMultipleLocations = (website: WebsiteLink): boolean => {
  if (website.countries && website.countries.length > 1) {
    return true;
  }
  if (website.cities && website.cities.length > 1) {
    return true;
  }
  return false;
};

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  scrollContainer?: React.RefObject<HTMLElement>;
  priority?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
}

const LazyImage = memo(function LazyImage({ src, alt, className, scrollContainer, priority = false, fetchPriority = 'auto' }: LazyImageProps) {
 const [imageSrc, setImageSrc] = useState<string>(src);
  
  useEffect(() => {
    setImageSrc(src);
  }, [src]);

  return (
    <div className="w-full h-full bg-black">
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={className}
          loading={priority ? "eager" : "lazy"}
          {...(priority && fetchPriority !== 'auto' ? { fetchpriority: fetchPriority } : {})}
        />
      )}
    </div>
  );
});

interface WebsiteCardProps {
  website: WebsiteLink;
  onMapClick?: (website: WebsiteLink) => void;
  showLocationInfo: boolean;
  showOnlyMultiLocation: boolean;
  scrollContainer?: React.RefObject<HTMLElement>;
  selectedContinents?: string[];
  selectedCountries?: string[];
  selectedCities?: string[];
  priority?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
}

// Memoized WebsiteCard component to prevent re-renders when parent re-renders
const WebsiteCard = memo(function WebsiteCard({ website, onMapClick, showLocationInfo, showOnlyMultiLocation, scrollContainer, selectedContinents, selectedCountries, selectedCities, priority, fetchPriority }: WebsiteCardProps) {
  const handleClick = () => {
    if (onMapClick) {
      onMapClick(website);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Determine if we should show location badges
  const shouldShowLocationBadges = () => {
    if (!showLocationInfo) return false;
    
    // Never show location badges on world maps (they have no location data)
    if (website.world === true) return false;
    
    // Check if map is at parent level (above the current filter level)
    const isParentLevel = () => {
      if (selectedCities && selectedCities.length > 0) {
        // Filtering by city - parent levels are: country, continent, world
        return (website.countries && (!website.cities || website.cities.length === 0)) ||
               (website.continent && (!website.countries || website.countries.length === 0));
      }
      if (selectedCountries && selectedCountries.length > 0) {
        // Filtering by country - parent levels are: continent, world
        return website.continent && (!website.countries || website.countries.length === 0);
      }
      // For continent filter, world is parent level (but world maps don't show badges anyway)
      return false;
    };
    
    // Parent level maps always show badges (provides context)
    if (isParentLevel()) {
      return true;
    }
    
    // Check if badge would be redundant (exact match at current filter level)
    if (selectedCities && selectedCities.length > 0 && website.cities) {
      if (website.cities.length === 1 && selectedCities.includes(website.cities[0])) {
        return false; // Redundant - we're already filtering by this city
      }
    }
    
    if (selectedCountries && selectedCountries.length > 0 && website.countries && (!website.cities || website.cities.length === 0)) {
      if (website.countries.length === 1 && selectedCountries.includes(website.countries[0])) {
        return false; // Redundant - we're already filtering by this country
      }
    }
    
    if (selectedContinents && selectedContinents.length > 0 && website.continent && (!website.countries || website.countries.length === 0)) {
      if (selectedContinents.includes(website.continent)) {
        return false; // Redundant - we're already filtering by this continent
      }
    }
    
    // For multi-location maps at current level, apply the showOnlyMultiLocation rule
    if (showOnlyMultiLocation) {
      return hasMultipleLocations(website);
    }
    return true;
  };

  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="group relative rounded-lg border border-white/40 overflow-hidden hover:border-[var(--accent-blue)] hover:shadow-[0_0_20px_rgba(var(--accent-blue-rgb),0.4)] cursor-pointer bg-white/8 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent flex flex-col"
      style={{ aspectRatio: '400 / 200' }}
      aria-label={`View details for ${website.title}${website.description ? `: ${website.description}` : ''}`}
    >
      {/* Image */}
      <div className="relative w-full h-[45%] bg-black overflow-hidden">
        {website.imageUrl && (
          <LazyImage
            src={website.imageUrl}
            alt={`Screenshot of ${website.title} map`}
            className="w-full h-full object-cover transition-transform duration-100 group-hover:scale-105"
            scrollContainer={scrollContainer}
            priority={priority}
            fetchPriority={fetchPriority}
          />
        )}
        
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        
        {(() => {
          const locationDisplay = shouldShowLocationBadges() 
            ? getLocationDisplay(website, true, selectedContinents, selectedCountries, selectedCities)
            : null;
          const additionalCount = getAdditionalLocationCount(website, true);
          
          return locationDisplay && (
            <div className="absolute top-1 right-1 flex flex-col items-end gap-[1px] z-10">
              <div className="bg-black/70 px-2 py-1 rounded text-xs text-white/70" style={{ fontWeight: '600' }}>
                {locationDisplay}
              </div>
              {additionalCount > 0 && (
                <div className="bg-black/70 px-2 py-0.5 rounded text-white/70" style={{ fontWeight: '600', fontSize: '0.65rem' }}>
                  +{additionalCount}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      
      {/* Background with text */}
      <div className="flex-1 flex flex-col justify-center items-start p-3">
        <p className="text-white text-lg md:text-base text-left font-bold">{website.title}</p>
        {website.description && (
          <p className="text-white/95 text-base md:text-sm line-clamp-2 mt-1 text-left">
            {website.description}
          </p>
        )}
      </div>
    </button>
  );
});

interface WebsiteGridProps {
  websites: WebsiteLink[];
  onMapClick?: (website: WebsiteLink) => void;
  showLocationInfo?: boolean;
  showOnlyMultiLocation?: boolean;
  scrollContainer?: React.RefObject<HTMLElement>;
  selectedContinents?: string[];
  selectedCountries?: string[];
  selectedCities?: string[];
}

export function WebsiteGrid({ websites, onMapClick, showLocationInfo = false, showOnlyMultiLocation = false, scrollContainer, selectedContinents, selectedCountries, selectedCities }: WebsiteGridProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
      {websites.map((website, index) => (
        <WebsiteCard
          key={website.id}
          website={website}
          onMapClick={onMapClick}
          showLocationInfo={showLocationInfo}
          showOnlyMultiLocation={showOnlyMultiLocation}
          scrollContainer={scrollContainer}
          selectedContinents={selectedContinents}
          selectedCountries={selectedCountries}
          selectedCities={selectedCities}
          priority={index < 8}
          fetchPriority={index === 0 ? 'high' : 'auto'}
        />
      ))}
    </div>
  );
}

// Sectioned Website Grid Component
interface SectionedWebsiteGridProps {
  sections: MapSection[];
  onMapClick?: (website: WebsiteLink) => void;
  showLocationInfo?: boolean;
  scrollContainer?: React.RefObject<HTMLElement>;
  selectedContinents?: string[];
  selectedCountries?: string[];
  selectedCities?: string[];
  selectedWorldMaps?: boolean;
  selectedCategories?: string[];
  selectedSearchTags?: string[];
  activeSectionType?: 'world' | 'city' | 'country' | 'continent' | null;
}

export function SectionedWebsiteGrid({
  sections,
  onMapClick,
  showLocationInfo = false,
  scrollContainer,
  selectedContinents,
  selectedCountries,
  selectedCities,
  selectedWorldMaps,
  selectedCategories,
  selectedSearchTags,
  activeSectionType
}: SectionedWebsiteGridProps) {
  // Local state for manually collapsed sections - initialized with auto-collapse logic
  const [manuallyCollapsed, setManuallyCollapsed] = useState<Set<string>>(() => {
    const hasNoLocationFilters = !selectedWorldMaps &&
                                  (!selectedContinents || selectedContinents.length === 0) &&
                                  (!selectedCountries || selectedCountries.length === 0) &&
                                  (!selectedCities || selectedCities.length === 0);
    
    const hasNoTagOrCategoryFilters = (!selectedCategories || selectedCategories.length === 0) &&
                                       (!selectedSearchTags || selectedSearchTags.length === 0);
    
    // BRANCH 1: No filters at all → Default collapse state
    if (hasNoLocationFilters && hasNoTagOrCategoryFilters) {
      return new Set(['continent', 'country', 'city']);
    } 
    // BRANCH 2: Location filters exist → Smart content-based section expansion
    else if (!hasNoLocationFilters) {
      // Determine selected location level
      let selectedLevel: 'city' | 'country' | 'continent' | 'world' | null = null;
      if (selectedCities && selectedCities.length > 0) selectedLevel = 'city';
      else if (selectedCountries && selectedCountries.length > 0) selectedLevel = 'country';
      else if (selectedContinents && selectedContinents.length > 0) selectedLevel = 'continent';
      else if (selectedWorldMaps) selectedLevel = 'world';
      
      if (!selectedLevel) {
        // Shouldn't happen, but fallback to no collapse
        return new Set();
      }
      
      // Define hierarchy order (broad to specific: world → continent → country → city)
      const hierarchyOrder: Array<'world' | 'continent' | 'country' | 'city'> = ['world', 'continent', 'country', 'city'];
      const selectedLevelIndex = hierarchyOrder.indexOf(selectedLevel);
      
      // PHASE 1: Check selected level + downward for maps
      let foundMapsInSelectedOrBelow = false;
      for (let i = selectedLevelIndex; i < hierarchyOrder.length; i++) {
        const section = sections.find(s => s.type === hierarchyOrder[i]);
        if (section && section.maps.length > 0) {
          foundMapsInSelectedOrBelow = true;
          break;
        }
      }
      
      if (foundMapsInSelectedOrBelow) {
        // Maps found in selected or lower levels → Use current behavior
        // Collapse everything ABOVE selected level
        const initialCollapsed = new Set<string>();
        for (let i = 0; i < selectedLevelIndex; i++) {
          initialCollapsed.add(hierarchyOrder[i]);
        }
        return initialCollapsed;
      } else {
        // PHASE 2: No maps in selected/lower levels → Check upward for first section with maps
        let sectionWithMaps: string | null = null;
        for (let i = selectedLevelIndex - 1; i >= 0; i--) {
          const section = sections.find(s => s.type === hierarchyOrder[i]);
          if (section && section.maps.length > 0) {
            sectionWithMaps = hierarchyOrder[i];
            break;
          }
        }
        
        if (sectionWithMaps) {
          // Found maps in parent level → Show ONLY that section
          const initialCollapsed = new Set<string>();
          hierarchyOrder.forEach(level => {
            if (level !== sectionWithMaps) {
              initialCollapsed.add(level);
            }
          });
          return initialCollapsed;
        } else {
          // No maps found anywhere → Collapse all (empty state)
          return new Set(['world', 'continent', 'country', 'city']);
        }
      }
    } 
    // BRANCH 3: Category/tags only (no location) → Expand all sections
    else {
      return new Set();
    }
  });
  // Local state for manually expanded sections (overrides auto-collapse)
  const [manuallyExpanded, setManuallyExpanded] = useState<Set<string>>(new Set());
  
  // Ref to access current collapse state in observer callback (avoid stale closures)
  const manuallyCollapsedRef = useRef<Set<string>>(manuallyCollapsed);
  
  // Sync ref with state changes
  useEffect(() => {
    manuallyCollapsedRef.current = manuallyCollapsed;
  }, [manuallyCollapsed]);
  
  // Scroll lock state - prevents dvh recalculation during browser chrome collapse
  const [lockedCenterValue, setLockedCenterValue] = useState<string | null>(null);
  const scrollLockTimeoutRef = useRef<number>();
  
  // Track if sections were already collapsed (prevents lock during collapse transition)
  const wasAlreadyCollapsed = useRef(false);
  
  // Refs for each section to enable scroll-to-section
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Track previous section map counts to detect newly-filled sections
  const prevSectionMapCounts = useRef<Map<string, number>>(new Map());
  
  // ============================================================
  // GLOW EFFECT STATE & REFS
  // ============================================================
  
  // State: Track which sections are currently glowing (Set allows multiple simultaneous glows)
  const [glowingSections, setGlowingSections] = useState<Set<string>>(new Set());
  
  // Ref: IntersectionObserver instance
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Ref: Timeout IDs for removing glow after animation completes
  const glowTimeoutRefs = useRef<Map<string, number>>(new Map());
  
  // Ref: Map of section type → DOM element for observation
  const sectionHeaderRefs = useRef<Map<string, HTMLElement>>(new Map());
  
  // Ref: Track intersection state to detect entry vs exit
  const intersectionState = useRef<Map<string, boolean>>(new Map());
  
  // Memoize hasActiveFilters to prevent unnecessary re-renders
  const hasActiveFilters = useMemo(() => {
    return selectedWorldMaps || 
      (selectedContinents && selectedContinents.length > 0) ||
      (selectedCountries && selectedCountries.length > 0) ||
      (selectedCities && selectedCities.length > 0) ||
      (selectedCategories && selectedCategories.length > 0) ||
      (selectedSearchTags && selectedSearchTags.length > 0);
  }, [
    selectedWorldMaps,
    selectedContinents,
    selectedCountries,
    selectedCities,
    selectedCategories,
    selectedSearchTags
  ]);
  
  // Detect newly-filled sections during "no filters" transition to prevent unwanted animation
  const sectionsToKeepCollapsed = useMemo(() => {
    const hasNoFilters = !selectedWorldMaps && 
      (!selectedContinents || selectedContinents.length === 0) &&
      (!selectedCountries || selectedCountries.length === 0) &&
      (!selectedCities || selectedCities.length === 0) &&
      (!selectedCategories || selectedCategories.length === 0) &&
      (!selectedSearchTags || selectedSearchTags.length === 0);
    
    if (!hasNoFilters) {
      // Filters are active - update tracking map for next transition
      sections.forEach(section => {
        if (section.type) {
          prevSectionMapCounts.current.set(section.type, section.maps.length);
        }
      });
      return new Set<string>();
    }
    
    // Transitioning to no filters - detect sections that were empty and are now filled
    const newlyFilled = new Set<string>();
    sections.forEach(section => {
      if (section.type) {
        const prevCount = prevSectionMapCounts.current.get(section.type) || 0;
        const currentCount = section.maps.length;
        
        // Section was empty (0 maps) and now has maps = newly filled
        if (prevCount === 0 && currentCount > 0) {
          newlyFilled.add(section.type);
        }
        
        // Update tracking for next render
        prevSectionMapCounts.current.set(section.type, currentCount);
      }
    });
    
    return newlyFilled;
  }, [sections, selectedWorldMaps, selectedContinents, selectedCountries, selectedCities, selectedCategories, selectedSearchTags]);
  
  // Reset collapse state when all filters are cleared (Maps tab only - component unmounts on other tabs)
  useLayoutEffect(() => {
    const hasNoFilters = !selectedWorldMaps && 
      (!selectedContinents || selectedContinents.length === 0) &&
      (!selectedCountries || selectedCountries.length === 0) &&
      (!selectedCities || selectedCities.length === 0) &&
      (!selectedCategories || selectedCategories.length === 0) &&
      (!selectedSearchTags || selectedSearchTags.length === 0);
    
    if (hasNoFilters) {
      // Collapse all sections when filters are cleared (clean slate)
      setManuallyCollapsed(new Set(['world', 'continent', 'country', 'city']));
      setManuallyExpanded(new Set());
    }
  }, [selectedContinents, selectedCountries, selectedCities, selectedWorldMaps, selectedCategories, selectedSearchTags]);
  
  // When parent requests a section to be active, replicate initialization logic as directive
  useLayoutEffect(() => {
    // Verify that activeSectionType matches actual filter state (prevent stale state bugs)
    const hasMatchingFilters = (() => {
      if (!activeSectionType) return false;
      
      switch (activeSectionType) {
        case 'city':
          return selectedCities && selectedCities.length > 0;
        case 'country':
          return selectedCountries && selectedCountries.length > 0;
        case 'continent':
          return selectedContinents && selectedContinents.length > 0;
        case 'world':
          return selectedWorldMaps === true;
        default:
          return false;
      }
    })();
    
    if (activeSectionType && hasMatchingFilters) {
      // Location filter active - use full two-phase initialization logic
      const hierarchyOrder: Array<'world' | 'continent' | 'country' | 'city'> = ['world', 'continent', 'country', 'city'];
      const selectedLevelIndex = hierarchyOrder.indexOf(activeSectionType);
      
      // PHASE 1: Check selected level + downward for maps
      let foundMapsInSelectedOrBelow = false;
      for (let i = selectedLevelIndex; i < hierarchyOrder.length; i++) {
        const section = sections.find(s => s.type === hierarchyOrder[i]);
        if (section && section.maps.length > 0) {
          foundMapsInSelectedOrBelow = true;
          break;
        }
      }
      
      if (foundMapsInSelectedOrBelow) {
        // Maps found in selected or lower levels → Collapse everything ABOVE selected level
        const newCollapsed = new Set<string>();
        for (let i = 0; i < selectedLevelIndex; i++) {
          newCollapsed.add(hierarchyOrder[i]);
        }
        setManuallyCollapsed(newCollapsed);
        setManuallyExpanded(new Set());
      } else {
        // PHASE 2: No maps in selected/lower levels → Check upward for first section with maps
        let sectionWithMaps: string | null = null;
        for (let i = selectedLevelIndex - 1; i >= 0; i--) {
          const section = sections.find(s => s.type === hierarchyOrder[i]);
          if (section && section.maps.length > 0) {
            sectionWithMaps = hierarchyOrder[i];
            break;
          }
        }
        
        if (sectionWithMaps) {
          // Found maps in parent level → Show ONLY that section
          const newCollapsed = new Set<string>();
          hierarchyOrder.forEach(level => {
            if (level !== sectionWithMaps) {
              newCollapsed.add(level);
            }
          });
          setManuallyCollapsed(newCollapsed);
          setManuallyExpanded(new Set());
        } else {
          // No maps found anywhere → Collapse all (empty state)
          setManuallyCollapsed(new Set(['world', 'continent', 'country', 'city']));
          setManuallyExpanded(new Set());
        }
      }
    } else {
      // No location filter, but if category/tag filters exist, expand all sections
      const hasNoLocationFilters = !selectedWorldMaps &&
        (!selectedContinents || selectedContinents.length === 0) &&
        (!selectedCountries || selectedCountries.length === 0) &&
        (!selectedCities || selectedCities.length === 0);
      
      const hasCategoryOrTagFilters = (selectedCategories && selectedCategories.length > 0) ||
        (selectedSearchTags && selectedSearchTags.length > 0);
      
      if (hasNoLocationFilters && hasCategoryOrTagFilters) {
        // Category/tag only - expand all sections
        setManuallyCollapsed(new Set());
        setManuallyExpanded(new Set());
      }
    }
  }, [activeSectionType, sections, selectedWorldMaps, selectedContinents, selectedCountries, selectedCities, selectedCategories, selectedSearchTags]);
  
  // ============================================================
  // GLOW EFFECT: IntersectionObserver for Section Headers
  // ============================================================
  useEffect(() => {
    // Early return if no active filters or no scroll container
    if (!hasActiveFilters || !scrollContainer?.current) {
      // Cleanup when filters are cleared
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      setGlowingSections(new Set());
      glowTimeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      glowTimeoutRefs.current.clear();
      return;
    }

    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionType = entry.target.getAttribute('data-section-type');
          if (!sectionType) return;

          // Get previous intersection state
          const wasIntersecting = intersectionState.current.get(sectionType);
          const isIntersecting = entry.isIntersecting;
         
          // Update intersection state
          intersectionState.current.set(sectionType, isIntersecting);

          // Check if this section is the first expanded section (topmost visible)
          const hierarchyOrder = ['world', 'continent', 'country', 'city'];
          const currentIndex = hierarchyOrder.indexOf(sectionType);
          const hasExpandedSectionsAbove = currentIndex > 0 && hierarchyOrder.slice(0, currentIndex).some(type => {
            const section = sections.find(s => s.type === type);
            return section && section.maps.length > 0 && !manuallyCollapsedRef.current.has(type);
          });

          // Only trigger glow on ENTRY (not exit) and prevent re-glow
          // Block glow if this is the first expanded section (no expanded sections above)
          if (!wasIntersecting && isIntersecting && !glowingSections.has(sectionType) && hasExpandedSectionsAbove) {
            
            // Add section to glowing set
            setGlowingSections(prev => new Set(prev).add(sectionType));

            // Clear any existing timeout for this section
            const existingTimeout = glowTimeoutRefs.current.get(sectionType);
            if (existingTimeout) {
              clearTimeout(existingTimeout);
            }

            // Remove from glowing set after 1.0 seconds (animation duration)
            const timeoutId = window.setTimeout(() => {
              setGlowingSections(prev => {
                const next = new Set(prev);
                next.delete(sectionType);
                return next;
              });
              glowTimeoutRefs.current.delete(sectionType);
            }, 1000);

            glowTimeoutRefs.current.set(sectionType, timeoutId);
          }
        });
      },
      {
        root: scrollContainer.current,
        rootMargin: '0px', // Full viewport - triggers on entry from any direction
        threshold: 0
      }
    );

    // Initialize: Observe all expanded sections and mark as "already seen"
    // This prevents glow on initial expand when section is already in viewport
    sectionHeaderRefs.current.forEach((element, sectionType) => {
      // Mark as "already seen" to prevent glow on expand
      intersectionState.current.set(sectionType, true);
      observer.observe(element);
    });

    observerRef.current = observer;

    // Cleanup on unmount or dependency change
    return () => {
      observer.disconnect();
      glowTimeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      glowTimeoutRefs.current.clear();
    };
   }, [hasActiveFilters, scrollContainer, sections]);
  
  // LCP Optimization: Lock in priority for first 8 World maps on initial render
  // This prevents priority from changing when sections are re-shuffled or collapsed/expanded
  const initialPriorityIds = useRef<Set<string> | null>(null);
  const initialHighPriorityId = useRef<string | null>(null);
  
  // Capture the first 8 World map IDs on first render only
  if (initialPriorityIds.current === null) {
    const worldSection = sections.find(s => s.type === 'world');
    if (worldSection && worldSection.maps.length > 0) {
      const firstEight = worldSection.maps.slice(0, 8);
      initialPriorityIds.current = new Set(firstEight.map(m => m.id));
      initialHighPriorityId.current = firstEight[0]?.id || null;
    } else {
      // No World maps on initial render
      initialPriorityIds.current = new Set();
      initialHighPriorityId.current = null;
    }
  }
  
  // Determine if section is expanded
  const isExpanded = (section: MapSection): boolean => {
    if (!section.label) return true;
    
    if (section.maps.length === 0) return false;
    
    // Check if manually expanded by user (overrides everything, including sectionsToKeepCollapsed)
    if (manuallyExpanded.has(section.type || '')) {
      return true;
    }
    
    // Prevent newly-filled sections from expanding during "no filters" transition
    if (sectionsToKeepCollapsed.has(section.type || '')) {
      return false;
    }
    
    if (manuallyCollapsed.has(section.type || '')) {
      return false;
    }
    
    return true;
  };

  const toggleSection = (section: MapSection) => {
    if (!section.type) return;
    
    // Detect "all maps setup" (no filters active)
    const isAllMapsSetup = !selectedWorldMaps && 
                           (!selectedContinents || selectedContinents.length === 0) &&
                           (!selectedCountries || selectedCountries.length === 0) &&
                           (!selectedCities || selectedCities.length === 0) &&
                           (!selectedCategories || selectedCategories.length === 0) &&
                           (!selectedSearchTags || selectedSearchTags.length === 0);
    
    const currentlyExpanded = isExpanded(section);
    
    // Detect if we're opening from all-closed state
    const openingFromAllClosed = !currentlyExpanded && allSectionsCollapsed;
    
    // Detect if we're collapsing to all-closed state (collapsing the last expanded section)
    const collapsingToAllClosed = currentlyExpanded && 
      sections.filter(s => s.label && s.maps.length > 0 && isExpanded(s)).length === 1;
    
    // Disable transition BEFORE state change if opening from all-closed
    if (openingFromAllClosed) {
      setEnableTransition(false);
    }
    
    // Apply pointer-events: none during collapse-to-all-closed animation
    if (collapsingToAllClosed) {
      setIsCollapsingToAllClosed(true);
      setTimeout(() => {
        setIsCollapsingToAllClosed(false);
      }, 300); // Match CSS transition duration
    }
    
    if (isAllMapsSetup) {
      // ACCORDION MODE: Only one section expanded at a time in "all maps" state
      if (currentlyExpanded) {
        // Collapsing: just add to collapsed set (allow all sections to be collapsed)
        setManuallyCollapsed(prev => new Set(prev).add(section.type!));
        setManuallyExpanded(new Set());
      } else {
        // Expanding: collapse all others, expand only this one
        const allSectionTypes = ['world', 'continent', 'country', 'city'];
        const othersCollapsed = allSectionTypes.filter(t => t !== section.type);
        setManuallyCollapsed(new Set(othersCollapsed));
        setManuallyExpanded(new Set([section.type!])); // ✅ FIX: Add expanding section to override sectionsToKeepCollapsed
      }
    } else {
      // STANDARD MODE: When filters are active, use existing behavior
      if (currentlyExpanded) {
        // User is collapsing: add to collapsed, remove from expanded
        setManuallyCollapsed(prev => new Set(prev).add(section.type!));
        setManuallyExpanded(prev => {
          const next = new Set(prev);
          next.delete(section.type!);
          return next;
        });
      } else {
        // User is expanding
        if (manuallyCollapsed.has(section.type)) {
          // Was manually collapsed: just remove from collapsed
          setManuallyCollapsed(prev => {
            const next = new Set(prev);
            next.delete(section.type!);
            return next;
          });
        } else {
          // Was auto-collapsed: add to manually expanded to override auto-collapse
          setManuallyExpanded(prev => new Set(prev).add(section.type!));
        }
      }
    }
    
    // Re-enable transition after state changes and DOM updates
    if (openingFromAllClosed) {
      requestAnimationFrame(() => {
        setEnableTransition(true);
      });
    }
    
    // Scroll to section top when expanding - 250ms animation
    if (!currentlyExpanded && scrollContainer?.current && section.type) {
      const container = scrollContainer.current;
      const expandingSectionIndex = sections.findIndex(s => s.type === section.type);
      
      // Wait for DOM to update before calculating offsetTop
      requestAnimationFrame(() => {
        // Find the highest collapsed section above (if any)
        const findScrollTarget = () => {
          // In "all maps" state (no filters), always scroll to World to show full hierarchy
          if (isAllMapsSetup) {
            const worldSection = sections.find(s => s.type === 'world');
            return worldSection || section;
          }
          
          let highestCollapsedIndex = null;
          
          // Scan upward from expanding section
          for (let i = expandingSectionIndex - 1; i >= 0; i--) {
            const currentSection = sections[i];
            
            // If we hit an expanded section, stop searching
            if (isExpanded(currentSection)) {
              break;
            }
            
            // If collapsed and has content, track it
            if (!isExpanded(currentSection) && currentSection.maps.length > 0) {
              highestCollapsedIndex = i;
            }
          }
          
          // Return the highest collapsed section, or the expanding section itself
          return highestCollapsedIndex !== null 
            ? sections[highestCollapsedIndex] 
            : section;
        };
        
        const targetSection = findScrollTarget();
        const targetSectionType = targetSection.type;
        const sectionElement = sectionRefs.current.get(targetSectionType);
        
        if (sectionElement) {
          const startScroll = container.scrollTop;
          const sectionTop = sectionElement.offsetTop;
          const duration = 250;
          const startTime = performance.now();
          
          const animateScroll = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            
            container.scrollTop = startScroll + (sectionTop - startScroll) * easedProgress;
            
            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            }
          };
          
          requestAnimationFrame(animateScroll);
        }
      });
    }
  };

  // Track global card index across all expanded sections for LCP optimization
  let globalCardIndex = 0;
  
  // Check if all sections are collapsed (for vertical centering)
  const allSectionsCollapsed = sections.every(section => {
    if (!section.label) return true; // Non-collapsible sections don't count
    return !isExpanded(section);
  });
  
  const [enableTransition, setEnableTransition] = useState(false);
  const [isCollapsingToAllClosed, setIsCollapsingToAllClosed] = useState(false);

  // Track previous collapsed state to make transition one-directional
  const prevAllCollapsed = useRef(allSectionsCollapsed);
  const isCollapsing = !prevAllCollapsed.current && allSectionsCollapsed;

  useEffect(() => {
    prevAllCollapsed.current = allSectionsCollapsed;
  }, [allSectionsCollapsed]);

  // Enable transitions after initial mount
  useEffect(() => {
    setEnableTransition(true);
  }, []);
  
  // Track collapsed state (updates AFTER render completes)
  useEffect(() => {
    wasAlreadyCollapsed.current = allSectionsCollapsed;
  }, [allSectionsCollapsed]);

  // Lock transform value during scroll to prevent dvh recalculation (Safari iOS chrome collapse fix)
  useEffect(() => {
    const container = scrollContainer?.current;
    
    // Lock transform when scrolling starts
    const lockTransform = () => {
      // Only lock when sections were ALREADY collapsed before scroll (not during collapse transition)
      if (allSectionsCollapsed && wasAlreadyCollapsed.current && !lockedCenterValue) {
        // Capture current viewport height and convert to pixels
        const currentDvhInPx = window.innerHeight * 0.5;
        setLockedCenterValue(`${currentDvhInPx}px`);
      }
      
      // Reset unlock timer on every scroll event
      clearTimeout(scrollLockTimeoutRef.current);
      scrollLockTimeoutRef.current = window.setTimeout(() => {
        // Unlock after 200ms of no scrolling (viewport is stable)
        setLockedCenterValue(null);
      }, 200);
    };
    
    // Listen to content container scroll (primary scroll mechanism)
    const handleContentScroll = () => lockTransform();
    
    // Listen to window scroll (background scroll / overscroll)
    const handleWindowScroll = () => lockTransform();
    
    // Attach both listeners with passive flag for performance
    if (container) {
      container.addEventListener('scroll', handleContentScroll, { passive: true });
    }
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleContentScroll);
      }
      window.removeEventListener('scroll', handleWindowScroll);
      clearTimeout(scrollLockTimeoutRef.current);
    };
  }, [allSectionsCollapsed, lockedCenterValue, scrollContainer]);

  return (
    <div 
      className={allSectionsCollapsed && enableTransition ? 'transition-transform duration-300' : ''}
      style={{
        transform: allSectionsCollapsed
          ? (lockedCenterValue 
              ? `translateY(calc(${lockedCenterValue} - 50%))` 
              : 'translateY(calc(50dvh - 50%))')
          : 'translateY(0)'
      }}
    >
      {sections.map((section, index) => {
        const hasContent = section.maps.length > 0;
        const sectionKey = `section-${section.type}-${index}`;
        const expanded = isExpanded(section);
        
        // Check if previous section is "effectively empty" (empty OR collapsed)
        const prevIndex = (index > 1 && sections[index - 1].maps.length === 0 && sections[index - 1].type === 'continent')
        ? index - 2
        : index - 1;
        const previousEffectivelyEmpty = index > 0 && (
        sections[prevIndex].maps.length === 0 || 
        !isExpanded(sections[prevIndex])
        );
        
        // Calculate margins: empty sections use same spacing as collapsed sections
        // Use 10px spacing in all-collapsed state (vertically centered), 7px otherwise
        const collapsedSpacing = allSectionsCollapsed ? '10px' : '7px';
        const marginTop = index === 0 ? '0px' : 
          (previousEffectivelyEmpty ? collapsedSpacing : '1.25rem');
        const marginBottom = expanded && hasContent ? '1.25rem' : collapsedSpacing;
        
        return (
          <div 
            key={sectionKey}
            ref={(el) => {
              if (el && section.type) {
                sectionRefs.current.set(section.type, el);
              }
            }}
          >
            {/* Show section header only if label exists */}
             {section.label && (hasContent || section.type !== 'continent') && (
              <div>
                {hasContent ? (
                  /* Clickable Section Header for sections with content */
                  <button
                    ref={(el) => {
                      if (el && section.type && hasActiveFilters) {
                        sectionHeaderRefs.current.set(section.type, el);
                      } else {
                        if (section.type) {
                          el?.classList.remove('section-header-glow');
                        }
                        sectionHeaderRefs.current.delete(section.type);
                      }
                    }}
                    data-section-type={section.type}
                    onClick={() => toggleSection(section)}
                    className={`w-full flex items-center justify-center group cursor-pointer hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded ${isCollapsingToAllClosed ? 'pointer-events-none' : ''} ${glowingSections.has(section.type) && expanded ? 'section-header-glow' : ''}`}
                    style={{
                      marginTop,
                      marginBottom
                    }}
                  >
                    {/* Left Line with center-outward fill animation */}
                    <div className="flex-1 h-[2px] relative overflow-hidden">
                      {/* Gradient background - always visible */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-white/50" />
                      
                      {/* Solid overlay - grows from center (right edge) outward to left */}
                      <div className={`absolute inset-0 bg-white/50 ${expanded ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-[800ms] origin-right`} />
                    </div>
                    
                    <div className="flex items-center gap-2 px-4">
                                            
                      {/* Section label */}
                      <span className="section-label text-white font-bold uppercase tracking-wider text-sm md:text-base">
                        {section.label}
                      </span>

                      {/* Green counter */}
                      <span className="text-green-400 font-bold text-sm md:text-base">
                        {section.maps.length}
                      </span>
                      
                      {/* Expand/Collapse chevron icon */}
                      {expanded ? (
                        <ChevronDown className="text-white transition-transform duration-75 size-4 md:size-4.5" />
                      ) : (
                        <ChevronDown className="text-white -rotate-90 transition-transform duration-75 size-4 md:size-4.5" />
                      )}
                    </div>
                    
                    {/* Right Line with center-outward fill animation */}
                    <div className="flex-1 h-[2px] relative overflow-hidden">
                      {/* Gradient background - always visible */}
                      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/40 to-white/50" />
                      
                      {/* Solid overlay - grows from center (left edge) outward to right */}
                      <div className={`absolute inset-0 bg-white/50 ${expanded ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-[800ms] origin-left`} />
                    </div>
                  </button>
                ) : (
                  /* Non-interactive Section Header for empty sections */
                  <div
                    className="w-full flex items-center justify-center"
                    style={{
                      marginTop,
                      marginBottom
                    }}
                  >
                    <div className={`flex-1 h-[2px] bg-gradient-to-r from-transparent via-white/20 to-white/20`} />
                    
                    <div className="flex items-center gap-2 px-4">
                                          
                      {/* Section label */}
                      <span className="text-white/50 text-xs md:text-sm uppercase tracking-wider">
                        {section.label}
                      </span>

                      {/* Green counter */}
                      <span className="text-green-400/50 text-xs md:text-sm font-bold">
                        {section.maps.length}
                      </span>
                      
                      {/* No icon for empty sections */}
                    </div>
                    
                    <div className={`flex-1 h-[2px] bg-gradient-to-l from-transparent via-white/20 to-white/20`} />
                  </div>
                )}
              </div>
            )}
            
            {/* Section grid - only show if expanded */}
            {expanded && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {section.maps.map((website) => {
                  const cardIndex = globalCardIndex++;
                  const isPriority = cardIndex < 8;
                  const fetchPri = cardIndex === 0 ? 'high' : 'auto';
                  
                  // LCP Optimization: Use locked priority for initial World map IDs
                  const isInitialPriorityMap = initialPriorityIds.current?.has(website.id) ?? false;
                  const finalPriority = isInitialPriorityMap ? true : isPriority;
                  const finalFetchPriority = website.id === initialHighPriorityId.current ? 'high' : 'auto';
                  
                  return (
                    <WebsiteCard
                      key={website.id}
                      website={website}
                      onMapClick={onMapClick}
                      showLocationInfo={showLocationInfo}
                      showOnlyMultiLocation={section.showOnlyMultiLocation}
                      scrollContainer={scrollContainer}
                      selectedContinents={selectedContinents}
                      selectedCountries={selectedCountries}
                      selectedCities={selectedCities}
                      priority={finalPriority}
                      fetchPriority={finalFetchPriority}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}